(function () {
  'use strict';

  const cache = {};
  const state = {
    product: null,
    handle: null,
    productUrl: null,
    selections: [],
    preOrder: null,
    finalSale: null,
    inStockMap: null,
    isOpen: false,
    trigger: null,
    cardDefaults: new WeakMap() // Each card price's Liquid HTML, before any variant was chosen
  };

  let drawer;
  let pageOverlay;

  function moneyFormat() {
    if (window.theme && window.theme.currencyCodeEnabled && window.theme.moneyWithCurrencyFormat) {
      return window.theme.moneyWithCurrencyFormat;
    }
    return (window.theme && window.theme.moneyFormat) || '${{amount}}';
  }

  function formatMoney(cents) {
    let amount = cents;
    if (typeof amount === 'string') {
      amount = amount.replace('.', '');
    }
    amount = Number(amount);
    if (isNaN(amount)) return '';

    const formatString = moneyFormat();
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const prec = precision == null ? 2 : precision;
      const thou = thousands == null ? ',' : thousands;
      const dec = decimal == null ? '.' : decimal;
      const parts = (number / 100.0).toFixed(prec).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thou);
      const centsPart = parts[1] ? dec + parts[1] : '';
      return dollars + centsPart;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return String(amount);

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(amount, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amount, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(amount, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  // Same prefix stripping as the one-tap swatch handler in main-collection.liquid.
  function stripPrefix(value, prefix) {
    if (!value) return null;
    const parts = value.split(prefix);
    return parts.length > 1 ? parts[1] : value;
  }

  function parseInStockMap(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }

  function productJsUrl(handle) {
    const root = (window.routes && window.routes.rootUrlWithoutSlash) || '';
    return root + '/products/' + encodeURIComponent(handle) + '.js';
  }

  function optionName(option) {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    return option.name || '';
  }

  function productOptions(product) {
    return (product && product.options) || [];
  }

  function uniqueOptionValues(product, optionIndex) {
    const fromOption = productOptions(product)[optionIndex];
    if (fromOption && Array.isArray(fromOption.values) && fromOption.values.length) {
      return fromOption.values.slice();
    }

    const key = 'option' + (optionIndex + 1);
    const seen = {};
    const values = [];
    (product.variants || []).forEach(function (variant) {
      const value = variant[key];
      if (value != null && value !== '' && !seen[value]) {
        seen[value] = true;
        values.push(value);
      }
    });
    return values;
  }

  function findVariant(product, selections) {
    if (!product || !product.variants) return null;
    return product.variants.find(function (variant) {
      return selections.every(function (value, index) {
        return variant['option' + (index + 1)] === value;
      });
    }) || null;
  }

  function isValueAvailable(product, selections, optionIndex, value) {
    const trial = selections.slice();
    trial[optionIndex] = value;
    return (product.variants || []).some(function (variant) {
      if (!variant.available) return false;
      for (let i = 0; i < trial.length; i++) {
        if (trial[i] == null || trial[i] === '') continue;
        if (variant['option' + (i + 1)] !== trial[i]) return false;
      }
      return true;
    });
  }

  function imageForVariant(product, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    if (product.featured_image) {
      return typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image.src;
    }
    return '';
  }

  function setOpen(open) {
    if (!drawer) return;
    state.isOpen = open;
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.classList.toggle('no-scroll', open);

    if (pageOverlay) {
      pageOverlay.classList.toggle('is-visible', open);
      if (open) {
        pageOverlay.addEventListener('click', closeDrawer);
      } else {
        pageOverlay.removeEventListener('click', closeDrawer);
      }
    }

    if (open) {
      drawer.style.maxHeight = window.innerHeight + 'px';
    }
  }

  function closeDrawer(event) {
    if (event) event.preventDefault();
    setOpen(false);
  }

  function els() {
    return {
      loading: drawer.querySelector('[data-quick-add-loading]'),
      error: drawer.querySelector('[data-quick-add-error]'),
      body: drawer.querySelector('[data-quick-add-body]'),
      image: drawer.querySelector('[data-quick-add-image]'),
      title: drawer.querySelector('[data-quick-add-title]'),
      variant: drawer.querySelector('[data-quick-add-variant]'),
      price: drawer.querySelector('[data-quick-add-price]'),
      options: drawer.querySelector('[data-quick-add-options]'),
      addButton: drawer.querySelector('[data-quick-add-submit]')
    };
  }

  function showState(kind, message) {
    const nodes = els();
    nodes.loading.hidden = kind !== 'loading';
    nodes.error.hidden = kind !== 'error';
    nodes.body.hidden = kind !== 'ready';
    if (kind === 'error' && message) {
      nodes.error.textContent = message;
    }
  }

  function setOptionLabel(label, name, selected) {
    label.textContent = '';
    label.appendChild(document.createTextNode(name + ': '));
    const selectedEl = document.createElement('span');
    selectedEl.className = 'QuickAdd__SelectedValue';
    selectedEl.textContent = selected || '';
    label.appendChild(selectedEl);
  }

  function updateSummary() {
    const product = state.product;
    const variant = findVariant(product, state.selections);
    const nodes = els();
    const src = imageForVariant(product, variant);

    if (src) {
      nodes.image.src = src;
      nodes.image.alt = product.title || '';
      nodes.image.hidden = false;
    } else {
      nodes.image.hidden = true;
    }

    nodes.title.textContent = '';
    const link = document.createElement('a');
    link.href = state.productUrl || ('/products/' + state.handle);
    link.textContent = product.title;
    nodes.title.appendChild(link);

    nodes.variant.textContent = state.selections.filter(Boolean).join(' / ');

    updateCardPrices(variant);

    const price = variant ? variant.price : product.price;
    const compare = variant ? variant.compare_at_price : product.compare_at_price;
    if (compare && compare > price) {
      nodes.price.innerHTML =
        '<span class="Price Price--highlight">' + formatMoney(price) + '</span>' +
        '<span class="Price Price--compareAt">' + formatMoney(compare) + '</span>';
    } else {
      nodes.price.innerHTML = '<span class="Price">' + formatMoney(price) + '</span>';
    }

    const canAdd = !!(variant && variant.available);
    nodes.addButton.disabled = !canAdd;
    nodes.addButton.textContent = canAdd
      ? (window.languages && window.languages.productFormAddToCart) || 'Add to cart'
      : (window.languages && window.languages.productFormSoldOut) || 'Sold out';
  }

  // The price and BFCM chip on the card that opened the drawer follow the chosen variant, as variants can
  // be priced differently (e.g. fuller cup sizes). Liquid renders each variant's HTML into a data attribute:
  // sections/card-nosto-plp-price.liquid (price) and snippets/promo-price.liquid (chip).
  function updateCardPrices(variant) {
    const card = state.trigger && state.trigger.closest('.Grid__Cell');
    if (!card) return;

    swapCardPrice(card.querySelector('[data-variant-prices]'), 'data-variant-prices', variant);
    const chip = card.querySelector('.promo-price[data-promo-prices]');
    swapCardPrice(chip && chip.querySelector('.promo-price__amount'), 'data-promo-prices', variant, chip);
  }

  function swapCardPrice(target, attribute, variant, source) {
    if (!target) return;
    const prices = JSON.parse((source || target).getAttribute(attribute));
    if (!state.cardDefaults.has(target)) state.cardDefaults.set(target, target.innerHTML);
    target.innerHTML = (variant && prices[variant.id]) || state.cardDefaults.get(target);
  }

  function renderOptions() {
    const product = state.product;
    const nodes = els();
    nodes.options.textContent = '';

    productOptions(product).forEach(function (option, optionIndex) {
      const name = optionName(option);
      const values = uniqueOptionValues(product, optionIndex);
      const selected = state.selections[optionIndex];

      const wrap = document.createElement('div');
      wrap.className = 'QuickAdd__Option';

      const label = document.createElement('span');
      label.className = 'QuickAdd__OptionLabel';
      setOptionLabel(label, name, selected);
      wrap.appendChild(label);

      const list = document.createElement('ul');
      list.className = 'QuickAdd__SwatchList';
      list.setAttribute('role', 'list');

      values.forEach(function (value) {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'QuickAdd__Swatch';
        button.textContent = value;

        const available = isValueAvailable(product, state.selections, optionIndex, value);
        if (!available) {
          button.classList.add('is-disabled');
          button.disabled = true;
        }
        if (selected === value) {
          button.classList.add('is-active');
        }

        button.addEventListener('click', function () {
          if (button.disabled) return;
          state.selections[optionIndex] = value;
          for (let i = optionIndex + 1; i < state.selections.length; i++) {
            if (!isValueAvailable(product, state.selections, i, state.selections[i])) {
              const fallback = uniqueOptionValues(product, i).find(function (candidate) {
                return isValueAvailable(product, state.selections, i, candidate);
              });
              state.selections[i] = fallback || null;
            }
          }
          renderOptions();
          updateSummary();
        });

        li.appendChild(button);
        list.appendChild(li);
      });

      wrap.appendChild(list);
      nodes.options.appendChild(wrap);
    });
  }

  function initSelections(product, preselect) {
    const selections = productOptions(product).map(function () {
      return null;
    });
    const preIndex =
      preselect && preselect.position != null ? Number(preselect.position) - 1 : -1;

    if (preIndex >= 0 && preIndex < selections.length && preselect.value) {
      selections[preIndex] = preselect.value;
    }

    for (let i = 0; i < selections.length; i++) {
      if (selections[i]) continue;
      const values = uniqueOptionValues(product, i);
      const pick = values.find(function (value) {
        return isValueAvailable(product, selections, i, value);
      });
      selections[i] = pick || values[0] || null;
    }

    if (preIndex >= 0 && preselect && preselect.value) {
      selections[preIndex] = preselect.value;
      for (let j = 0; j < selections.length; j++) {
        if (j === preIndex) continue;
        if (!isValueAvailable(product, selections, j, selections[j])) {
          const alt = uniqueOptionValues(product, j).find(function (value) {
            return isValueAvailable(product, selections, j, value);
          });
          selections[j] = alt || selections[j];
        }
      }
    }

    state.selections = selections;
  }

  function renderProduct(product, preselect) {
    state.product = product;
    initSelections(product, preselect);
    showState('ready');
    renderOptions();
    updateSummary();
  }

  function fetchProduct(handle) {
    if (cache[handle]) {
      return Promise.resolve(cache[handle]);
    }

    return fetch(productJsUrl(handle), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unable to load product');
        }
        return response.json();
      })
      .then(function (data) {
        cache[handle] = data;
        return data;
      });
  }

  function openForTrigger(trigger) {
    const handle = trigger.getAttribute('data-product-handle');
    if (!handle) return;

    state.handle = handle;
    state.trigger = trigger;
    state.productUrl = trigger.getAttribute('data-product-url') || ('/products/' + handle);
    state.product = null;
    state.selections = [];
    state.preOrder = stripPrefix(trigger.getAttribute('data-pre-order'), 'PRE-ORDER: ');
    state.finalSale = stripPrefix(trigger.getAttribute('data-final-sale'), 'FINAL SALE: ');
    state.inStockMap = parseInStockMap(trigger.getAttribute('data-in-stock-map'));

    const preselect = {
      position: trigger.getAttribute('data-option-position'),
      value: trigger.getAttribute('data-option-value')
    };

    const nodes = els();
    nodes.addButton.disabled = true;
    nodes.error.hidden = true;

    setOpen(true);
    showState('loading');

    fetchProduct(handle)
      .then(function (product) {
        if (state.handle !== handle) return;
        renderProduct(product, preselect);
      })
      .catch(function () {
        if (state.handle !== handle) return;
        showState('error', 'Sorry, this product could not be loaded. Please try again.');
      });
  }

  function addToCart() {
    const variant = findVariant(state.product, state.selections);
    const nodes = els();
    if (!variant || !variant.available || nodes.addButton.disabled) return;

    nodes.addButton.disabled = true;
    const previousLabel = nodes.addButton.textContent;
    nodes.addButton.textContent = 'Adding…';

    const properties = {};
    if (state.finalSale) properties['FINAL SALE'] = state.finalSale;
    if (state.preOrder) properties['PRE-ORDER'] = state.preOrder;

    // STOQ only preloads per-variant stock on the PDP; give it the card's market stock so it
    // adds the pre-order note. Mirrors the one-tap swatch handler in main-collection.liquid.
    const stoq = window._RestockRocketConfig;
    if (state.inStockMap && stoq && state.inStockMap[variant.id] != null) {
      stoq.variantsInventoryPolicy = stoq.variantsInventoryPolicy || {};
      stoq.variantsInventoryQuantity = stoq.variantsInventoryQuantity || {};
      stoq.variantsInventoryPolicy[variant.id] = 'continue';
      stoq.variantsInventoryQuantity[variant.id] = state.inStockMap[variant.id] ? 1 : 0;
    }

    const cartAddUrl = ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';

    fetch(cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: 1, properties: properties }]
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            const message = (data && (data.description || data.message)) || 'Unable to add to cart';
            throw new Error(message);
          }
          return data;
        });
      })
      .then(function () {
        closeDrawer();
        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: variant.id,
            quantity: 1
          }
        }));
      })
      .catch(function (err) {
        showState('ready');
        renderOptions();
        updateSummary();
        const errorEl = els().error;
        errorEl.hidden = false;
        errorEl.textContent = err.message || "Sorry! Item couldn't be added to cart.";
      })
      .finally(function () {
        nodes.addButton.textContent = previousLabel;
        updateSummary();
      });
  }

  function onDocumentClick(event) {
    const trigger = event.target.closest('[data-action="open-quick-add"]');
    if (!trigger || trigger.classList.contains('disabled')) return;
    event.preventDefault();
    event.stopPropagation();
    openForTrigger(trigger);
  }

  function onKeyUp(event) {
    if (state.isOpen && event.key === 'Escape') {
      closeDrawer();
    }
  }

  function onResize() {
    if (state.isOpen) {
      drawer.style.maxHeight = window.innerHeight + 'px';
    }
  }

  function init() {
    drawer = document.getElementById('quick-add-drawer');
    if (!drawer) return;

    pageOverlay = document.querySelector('.PageOverlay');

    drawer.querySelectorAll('[data-action="close-drawer"]').forEach(function (btn) {
      btn.addEventListener('click', closeDrawer);
    });

    const addButton = drawer.querySelector('[data-quick-add-submit]');
    if (addButton) {
      addButton.addEventListener('click', addToCart);
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
